// Manufacturing validation of a generation result: the numbers the export
// dialog surfaces inline (never blocking). All figures derive from the same
// GenResult every exporter consumes.

import type { GenResult, Ring } from '../core/types'
import { signedArea, ringBBox, distToSegment } from './geometry'

// Webs are only searched below this cap; wider walls report as null
// ("comfortably wide"), which keeps the neighbor search exact and cheap.
export const WEB_SEARCH_CAP = 25

export interface ValidationReport {
  holeCount: number
  openAreaPct: number // hole area / boundary area * 100
  minHoleWidth: number | null // smallest hole's narrow dimension, mm
  minWeb: number | null // smallest wall between two holes, mm, exact; null when all webs exceed WEB_SEARCH_CAP
  stepEntityEstimate: number // rough STEP entity count for the panel solid
  stlTriangleEstimate: number // rough triangle count of the panel mesh
}

function ringArea(ring: Ring): number {
  return Math.abs(signedArea(ring))
}

function orient(px: number, py: number, qx: number, qy: number, rx: number, ry: number): number {
  return (qx - px) * (ry - py) - (qy - py) * (rx - px)
}

// Minimum distance between two segments: zero when they cross, otherwise the
// smallest endpoint-to-segment distance (which also covers touching and
// collinear overlap).
function segSegDist(a1: Ring[number], a2: Ring[number], b1: Ring[number], b2: Ring[number]): number {
  const o1 = orient(a1[0], a1[1], a2[0], a2[1], b1[0], b1[1])
  const o2 = orient(a1[0], a1[1], a2[0], a2[1], b2[0], b2[1])
  const o3 = orient(b1[0], b1[1], b2[0], b2[1], a1[0], a1[1])
  const o4 = orient(b1[0], b1[1], b2[0], b2[1], a2[0], a2[1])
  if (o1 > 0 !== o2 > 0 && o3 > 0 !== o4 > 0) return 0
  const A1 = { x: a1[0], y: a1[1] }
  const A2 = { x: a2[0], y: a2[1] }
  const B1 = { x: b1[0], y: b1[1] }
  const B2 = { x: b2[0], y: b2[1] }
  return Math.min(
    distToSegment(A1, B1, B2),
    distToSegment(A2, B1, B2),
    distToSegment(B1, A1, A2),
    distToSegment(B2, A1, A2),
  )
}

function ringRingDist(a: Ring, b: Ring): number {
  let min = Infinity
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i]
    const a2 = a[(i + 1) % a.length]
    for (let j = 0; j < b.length; j++) {
      const d = segSegDist(a1, a2, b[j], b[(j + 1) % b.length])
      if (d < min) {
        min = d
        if (min === 0) return 0
      }
    }
  }
  return min
}

function circleRingDist(cx: number, cy: number, r: number, ring: Ring): number {
  let min = Infinity
  const p = { x: cx, y: cy }
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]
    const b = ring[(i + 1) % ring.length]
    const d = distToSegment(p, { x: a[0], y: a[1] }, { x: b[0], y: b[1] })
    if (d < min) min = d
  }
  return Math.max(0, min - r)
}

// Smallest web between holes. Bounding circles on a spatial hash prefilter
// candidate pairs (the circle gap is a lower bound on the true web); every
// surviving pair gets an exact outline-to-outline distance, so the reported
// minimum is exact for all shapes whenever it is below WEB_SEARCH_CAP.
export function validateResult(result: GenResult): ValidationReport {
  const holes = result.holes
  const boundaryArea = ringArea(result.boundary)

  let holeArea = 0
  let minHoleWidth: number | null = null
  const centers: { x: number; y: number; r: number; circle: boolean; outer: Ring }[] = []

  for (const h of holes) {
    const outer = h.poly[0]
    let area = ringArea(outer)
    for (let i = 1; i < h.poly.length; i++) area -= ringArea(h.poly[i])
    holeArea += area

    let width: number
    if (h.circle) {
      width = h.circle.r * 2
      centers.push({ x: h.circle.cx, y: h.circle.cy, r: h.circle.r, circle: true, outer })
    } else {
      const bb = ringBBox(outer)
      width = Math.min(bb.maxX - bb.minX, bb.maxY - bb.minY)
      const cx = (bb.minX + bb.maxX) / 2
      const cy = (bb.minY + bb.maxY) / 2
      let r = 0
      for (const [x, y] of outer) r = Math.max(r, Math.hypot(x - cx, y - cy))
      centers.push({ x: cx, y: cy, r, circle: false, outer })
    }
    if (minHoleWidth === null || width < minHoleWidth) minHoleWidth = width
  }

  // Spatial hash over centers. Cell size 2*maxR + cap guarantees that any
  // pair outside the 3x3 neighborhood has web >= WEB_SEARCH_CAP, so the
  // minimum found is exact whenever it is below the cap.
  let minWeb: number | null = null
  if (centers.length > 1) {
    const maxR = Math.max(...centers.map((c) => c.r))
    const cell = Math.max(1e-6, maxR * 2 + WEB_SEARCH_CAP)
    const grid = new Map<string, number[]>()
    const key = (gx: number, gy: number) => `${gx},${gy}`
    centers.forEach((c, i) => {
      const gx = Math.floor(c.x / cell)
      const gy = Math.floor(c.y / cell)
      const k = key(gx, gy)
      const list = grid.get(k)
      if (list) list.push(i)
      else grid.set(k, [i])
    })
    for (let i = 0; i < centers.length; i++) {
      const a = centers[i]
      const gx = Math.floor(a.x / cell)
      const gy = Math.floor(a.y / cell)
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          for (const j of grid.get(key(gx + dx, gy + dy)) ?? []) {
            if (j <= i) continue
            const b = centers[j]
            // Bounding-circle gap is a lower bound on the true web: skip the
            // exact work when it cannot beat the minimum found so far.
            const bound = Math.hypot(a.x - b.x, a.y - b.y) - a.r - b.r
            if (bound >= WEB_SEARCH_CAP) continue
            if (minWeb !== null && bound >= minWeb) continue
            let web: number
            if (a.circle && b.circle) web = Math.max(0, bound)
            else if (a.circle) web = circleRingDist(a.x, a.y, a.r, b.outer)
            else if (b.circle) web = circleRingDist(b.x, b.y, b.r, a.outer)
            else web = ringRingDist(a.outer, b.outer)
            if (minWeb === null || web < minWeb) minWeb = web
          }
        }
      }
    }
  }

  if (minWeb !== null && minWeb >= WEB_SEARCH_CAP) minWeb = null

  // Rough entity count of the panel STEP: scaffolding plus per-ring cost.
  let entities = 60
  let outlineVerts = result.boundary.length
  for (const h of holes) {
    if (h.circle) entities += 25
    else for (const ring of h.poly) entities += ring.length * 14
    for (const ring of h.poly) outlineVerts += ring.length
  }
  entities += result.boundaryCircle ? 25 : result.boundary.length * 14
  // Panel mesh: each outline vertex yields two side triangles, and the two
  // faces triangulate to roughly the same vertex count again.
  const stlTriangleEstimate = outlineVerts * 4

  return {
    holeCount: holes.length,
    openAreaPct: boundaryArea > 0 ? (holeArea / boundaryArea) * 100 : 0,
    minHoleWidth,
    minWeb,
    stepEntityEstimate: entities,
    stlTriangleEstimate,
  }
}
